import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';

import { api } from '../lib/api.js';
import { Cargando, Error as AvisoError } from '../piezas/Estados.jsx';

/**
 * Las actividades de un tipo de servicio, en orden, con su duracion.
 *
 * La tabla existia desde la migracion 0004 y nadie la escribia: ni API, ni
 * pantalla, ni semilla. Cero filas desde el primer dia.
 *
 * Lo que se captura aqui es la duracion ESPERADA de un servicio. Sumada, es
 * lo que permite armar un programa de rollout con un numero —tantos sitios
 * por dia porque la visita dura esto— en vez de a ojo, y medir la
 * productividad de un ingeniero contra un esperado en vez de contra una
 * impresion.
 */

interface Renglon {
  id: string;
  orden: number;
  nombre: string;
  minutos_sugeridos: number;
}

/** Lo que se esta editando. Sin `id`: una actividad nueva todavia no lo tiene. */
interface EnEdicion {
  nombre: string;
  minutos: string;
}

/** Minutos a algo que se lee: 95 son "1 h 35 min". */
function enHoras(minutos: number): string {
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export function Actividades({
  servicio,
  puedeEscribir,
}: {
  /** La clave del tipo de servicio: `ser_inst`. */
  servicio: string;
  puedeEscribir: boolean;
}): JSX.Element {
  const [lista, setLista] = useState<EnEdicion[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  const cargar = useCallback(() => {
    api
      .obtener<Renglon[]>(`/v1/tipos-servicio/${encodeURIComponent(servicio)}/actividades`)
      .then(({ datos }) => {
        setLista(datos.map((a) => ({ nombre: a.nombre, minutos: String(a.minutos_sugeridos) })));
        setError(null);
      })
      .catch(setError);
  }, [servicio]);

  useEffect(() => cargar(), [cargar]);

  const total = (lista ?? []).reduce((s, a) => s + (Number.parseInt(a.minutos, 10) || 0), 0);

  function cambiar(i: number, campo: keyof EnEdicion, valor: string): void {
    setGuardado(false);
    setLista((previo) => (previo ?? []).map((a, j) => (j === i ? { ...a, [campo]: valor } : a)));
  }

  async function guardar(): Promise<void> {
    setGuardando(true);
    setGuardado(false);
    try {
      /* Se manda la lista COMPLETA, que es como la guarda el servidor: cada
         posicion se reescribe y lo que sobra del final se da de baja. Mandar
         solo lo cambiado obligaria a renumerar aqui, y renumerar a medias
         deja huecos. */
      const { datos } = await api.poner<Renglon[]>(
        `/v1/tipos-servicio/${encodeURIComponent(servicio)}/actividades`,
        {
          actividades: (lista ?? [])
            /* Un renglon en blanco es uno que se agrego y no se lleno: se
               descarta en vez de rechazar el guardado entero. */
            .filter((a) => a.nombre.trim() !== '')
            .map((a) => ({
              nombre: a.nombre.trim(),
              minutos_sugeridos: Number.parseInt(a.minutos, 10) || 1,
            })),
        },
      );
      /* Se adopta lo que CONTESTO la base, no lo que se mando: si algo se
         quedo fuera, la pantalla lo muestra en vez de seguir ensenando lo
         que se tecleo. */
      setLista(datos.map((a) => ({ nombre: a.nombre, minutos: String(a.minutos_sugeridos) })));
      setGuardado(true);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: '14px' }} data-prueba="panel-actividades">
      <div className="toolbar">
        <h3 style={{ margin: 0 }}>Actividades del servicio</h3>
        <span className="sp" />
        <span className="chip-dato" data-prueba="actividades-total">
          {enHoras(total)} por visita
        </span>
      </div>
      <p className="mini" style={{ marginTop: 0 }}>
        En orden, con la duracion esperada de cada una. De aqui sale la estimacion de un programa de
        rollout y el esperado contra el que se compara la hoja de servicio.
      </p>

      {error !== null && <AvisoError error={error} />}

      {lista === null ? (
        <Cargando />
      ) : lista.length === 0 && !puedeEscribir ? (
        <p className="muted" data-prueba="actividades-vacio">
          Este tipo de servicio no tiene actividades capturadas.
        </p>
      ) : (
        <div className="tabla-mapa">
          <table data-prueba="tabla-actividades">
            <thead>
              <tr>
                <th scope="col" style={{ width: 40 }}>
                  #
                </th>
                <th scope="col">Actividad</th>
                <th scope="col" className="num">
                  Minutos
                </th>
              </tr>
            </thead>
            <tbody>
              {lista.map((a, i) => (
                /* La llave es el INDICE, que normalmente es un error y aqui
                   es lo correcto: la identidad de una actividad es su
                   posicion. Asi lo decide la base —la unicidad
                   (tipo_servicio_id, orden) no es parcial— y asi lo guarda la
                   ruta. El `id` de la fila no sirve de llave porque una
                   actividad recien agregada todavia no tiene ninguno. */
                <tr key={i} data-prueba="fila-actividad">
                  <th scope="row">{i + 1}</th>
                  <td>
                    {puedeEscribir ? (
                      <input
                        value={a.nombre}
                        onChange={(e) => cambiar(i, 'nombre', e.target.value)}
                        aria-label={`Actividad ${i + 1}`}
                        style={{ width: '100%' }}
                        /* Enter aqui enviaria el formulario del tipo de
                           servicio, que es el dialogo que contiene a este
                           panel: guardaria el catalogo en vez de la lista. */
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void guardar();
                          }
                        }}
                        data-prueba={`actividad-${i + 1}`}
                      />
                    ) : (
                      a.nombre
                    )}
                  </td>
                  <td className="num">
                    {puedeEscribir ? (
                      <input
                        value={a.minutos}
                        onChange={(e) => cambiar(i, 'minutos', e.target.value)}
                        inputMode="numeric"
                        aria-label={`Minutos de la actividad ${i + 1}`}
                        style={{ width: 70, textAlign: 'right' }}
                        data-prueba={`minutos-${i + 1}`}
                      />
                    ) : (
                      a.minutos
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {puedeEscribir && lista !== null && (
        <div className="toolbar" style={{ marginTop: '10px' }}>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setGuardado(false);
              setLista([...lista, { nombre: '', minutos: '30' }]);
            }}
            data-prueba="agregar-actividad"
          >
            + Actividad
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setGuardado(false);
              setLista(lista.slice(0, -1));
            }}
            disabled={lista.length === 0}
            data-prueba="quitar-actividad"
          >
            Quitar la ultima
          </button>
          <span className="sp" />
          {guardado && (
            <span className="mini" role="status" data-prueba="actividades-guardado">
              Guardado
            </span>
          )}
          <button
            type="button"
            className="btn sm"
            onClick={() => void guardar()}
            disabled={guardando}
            data-prueba="guardar-actividades"
          >
            {guardando ? '...' : 'Guardar actividades'}
          </button>
        </div>
      )}
    </div>
  );
}
